import { useState } from 'react';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconWebhook } from '@components/icons/webhook';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { toEndpointMethodOption } from '../common';
import {
  useCreateIntegrationWebhook,
  useIntegration,
  useUpdateIntegrationWebhook,
} from '../integrations-hooks';
import {
  endpointMethods,
  type IntegrationWebhook,
  type IntegrationWebhookCreateType,
} from '../integrations-types';

export const useIntegrationWebhookCreateSidebar = () =>
  useSidebar(IntegrationWebhookCreateSidebar, { variant: 'REGULAR' });

const Form = createForm<IntegrationWebhookCreateType>();

const IntegrationWebhookCreateSidebar = ({
  integrationId,
  webhook,
}: {
  integrationId: string;
  webhook?: IntegrationWebhook;
}) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateIntegrationWebhook();
  const updateMutation = useUpdateIntegrationWebhook();
  const integrationQuery = useIntegration(integrationId);
  const isEdit = Boolean(webhook);
  const handleSubmit = async (data: IntegrationWebhookCreateType) => {
    const url = !showFullUrl && hasBaseUrl ? `${baseUrl}${formUrl}` : formUrl;
    const payload = { ...data, url };
    if (webhook) {
      await updateMutation.mutateAsync({ data: payload, shortId: webhook.shortId });
    } else {
      await createMutation.mutateAsync({
        ...payload,
        integration: integrationId,
        url: url || 'http://unsubscribable-url.50skills.com',
      });
    }
    hideSidebar();
  };
  const formStore = useCreateFormStore();
  const formUrl = formStore.useFieldValue('url') as string;
  const isDirty = formStore.useIsDirty();
  const isSubscribable = formStore.useFieldValue('isSubscribable') as boolean;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const allowedMethods = endpointMethods.filter(
    (method) => method !== 'GET' && method !== 'DELETE',
  );

  const baseUrl = integrationQuery.data?.baseUrl ?? '';
  const hasBaseUrl = Boolean(baseUrl);
  const initialShowFullUrl =
    !hasBaseUrl || Boolean(webhook?.url && !webhook.url.startsWith(baseUrl));
  const [showFullUrl, setShowFullUrl] = useState(initialShowFullUrl);
  const initialUrl = initialShowFullUrl
    ? (webhook?.url ?? '')
    : (webhook?.url ?? '').replace(baseUrl, '');

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEdit ? IconEdit : IconWebhook}
        iconColor="elementBlue100"
        title={isEdit ? t('editWebhook') : t('addWebhook')}
      />
      <Sidebar.Content>
        <Form onSubmit={handleSubmit} store={formStore}>
          <Form.TextField
            id="name"
            label={t('name')}
            validators={['required']}
            defaultValue={webhook?.name ?? ''}
          />
          <Form.Textarea
            id="description"
            label={t('description')}
            defaultValue={webhook?.description ?? ''}
          />
          <Form.Switch
            id="isSubscribable"
            label={t('subscribeWithExternalUrl')}
            defaultValue={webhook?.isSubscribable ?? true}
            isDisabled={Boolean(webhook)}
          />
          <Alert variant="info">
            <Text
              color="indigo100"
              font="regular14"
              text={isSubscribable ? t('subscribableDescription') : t('unsubscribableDescription')}
            />
          </Alert>
          {isSubscribable && (
            <>
              <Stack space={2}>
                <Stack space={1}>
                  <Form.TextField
                    id="url"
                    label={showFullUrl ? t('url') : t('path')}
                    validators={showFullUrl ? ['valid-http-url', 'required'] : ['required']}
                    defaultValue={initialUrl}
                    placeholder={
                      showFullUrl
                        ? 'e.g. https://journeys-api.50skills.app/v1/journeys/'
                        : 'e.g. /v1/journeys/'
                    }
                  />
                  {!showFullUrl && (
                    <Text font="regular12" text={`${baseUrl}${formUrl}`} color="textHelper" />
                  )}
                </Stack>
                {hasBaseUrl && (
                  <Stack orientation="horizontal" align="center" space={1}>
                    <Switch
                      value={showFullUrl}
                      onChange={(value) => {
                        formStore.actions.setValue('url', '');
                        setShowFullUrl(value);
                      }}
                    />
                    <Text font="regular14" text={t('useFullUrl')} />
                  </Stack>
                )}
              </Stack>
              <Form.Select
                id="method"
                label={t('method')}
                validators={['required']}
                options={allowedMethods.map(toEndpointMethodOption)}
                isClearable={false}
                defaultValue={webhook?.method ?? 'POST'}
                isDisabled={webhook?.webhookCreated}
              />
              <Form.JsonEditor
                id="exampleData"
                label={t('requestValues')}
                initialValue={webhook?.exampleData}
                isDisabled={webhook?.webhookCreated}
              />
            </>
          )}

          <Form.JsonEditor
            id="payloadData"
            label={t('payloadValues')}
            initialValue={webhook?.payloadData}
          />
          <CustomWebhookResponse webhook={webhook} />
          <Sidebar.Footer>
            <Form.Actions>
              <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit
                variant="primary-blue"
                text={isEdit ? t('core.save') : t('addWebhook')}
                isDisabled={!isDirty || isLoading}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const CustomWebhookResponse = ({ webhook }: { webhook?: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const [isEnabled, setIsEnabled] = useState(Boolean(webhook?.responseBody));

  return (
    <>
      <Switch
        label={t('customWebhookResponse')}
        value={isEnabled}
        onChange={() => setIsEnabled(!isEnabled)}
      />
      {isEnabled && (
        <>
          <Form.TextField
            id="responseBody"
            label={t('responseBody')}
            placeholder={t('responseBodyPlaceholder')}
            defaultValue={webhook?.responseBody ?? ''}
          />
          <Form.Select
            id="responseStatusCode"
            label={t('responseStatusCode')}
            defaultValue={String(webhook?.responseStatusCode ?? 200)}
            options={[
              { label: '200 OK', value: '200' },
              { label: '201 Created', value: '201' },
              { label: '202 Accepted', value: '202' },
            ]}
          />
        </>
      )}
    </>
  );
};

const locale = {
  en: {
    addWebhook: 'Add Webhook',
    editWebhook: 'Edit Webhook',
    name: 'Name',
    description: 'Description',
    url: 'URL',
    method: 'Method',
    requestValues: 'Request values used to subscribe',
    payloadValues: 'Payload values the webhook returns',
    subscribeWithExternalUrl: 'Subscribe using external url',
    subscribableDescription:
      "Automate the process of subscribing to a webhook by calling an external URL. This way, users won't need to manually insert the unique webhook URL into actions; it will be done automatically",
    unsubscribableDescription:
      'Create a webhook instance to get a unique webhook url that can then be used in other actions',
    path: 'Path',
    useFullUrl: 'Use full URL',
    customWebhookResponse: 'Custom webhook response',
    responseBody: 'Response body',
    responseBodyPlaceholder: 'Leave empty for default response',
    responseStatusCode: 'Status code',
  },
};
